import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import type { ProjectStats } from '../types/api';

interface TopIpsChartProps {
  stats: ProjectStats[] | null;
  selectedProject: string;
}

export function TopIpsChart({ stats, selectedProject }: TopIpsChartProps) {
  // Находим данные для выбранного проекта
  const projectData = stats?.find((s) => s.extensionName === selectedProject);

  if (!projectData || !projectData.ips.topIps || projectData.ips.topIps.length === 0) {
    return (
      <div className="bg-dark-100 rounded-lg p-6 shadow-xl border border-primary-900/30">
        <h2 className="text-2xl font-bold text-primary-400 mb-4 flex items-center gap-2">
          <span className="text-3xl">🌐</span>
          Топ IP-адресов
        </h2>
        <div className="text-center py-12 text-dark-500">
          <span className="text-5xl mb-4 block">📭</span>
          <p className="text-lg">Нет данных по IP-адресам</p>
          <p className="text-sm mt-2">Выберите проект с активностью</p>
        </div>
      </div>
    );
  }

  // Подготавливаем данные для графика (берем максимум топ-10)
  const chartData = projectData.ips.topIps.slice(0, 10).map((ipData) => ({
    ip: ipData.ip || 'Unknown',
    count: ipData.count,
    isAnomaly: ipData.isAnomaly,
    displayIp: ipData.ip ? (ipData.ip.length > 15 ? `${ipData.ip.slice(0, 12)}...` : ipData.ip) : 'Unknown',
  }));

  // Кастомный tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-dark-100 border border-primary-500 rounded-lg p-4 shadow-xl">
          <p className="font-bold text-primary-400 mb-2 font-mono">{data.ip}</p>
          <div className="space-y-1 text-sm">
            <p className="text-dark-700">
              <span className="font-semibold">Запросов:</span>{' '}
              <span className="text-orange-400 font-bold">{data.count.toLocaleString()}</span>
            </p>
            <p className="text-dark-700">
              <span className="font-semibold">Статус:</span>{' '}
              {data.isAnomaly ? (
                <span className="text-red-400 font-semibold">⚠️ Аномалия</span>
              ) : (
                <span className="text-green-400">✓ Нормальная активность</span>
              )}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-dark-100 rounded-lg p-6 shadow-xl border border-primary-900/30">
      <h2 className="text-2xl font-bold text-primary-400 mb-4 flex items-center gap-2">
        <span className="text-3xl">🌐</span>
        Топ IP-адресов
        <span className="text-lg text-dark-500 font-normal ml-2">
          ({projectData.extensionName})
        </span>
      </h2>

      <div className="mb-4 p-3 bg-dark-50 rounded-lg border border-dark-200">
        <div className="flex items-center justify-between">
          <p className="text-sm text-dark-600">
            <span className="font-semibold text-primary-400">💡 Подсказка:</span>{' '}
            Красные столбцы - аномальные IP с подозрительно высокой активностью.
          </p>
          <div className="text-sm font-semibold text-dark-600">
            Всего уникальных IP: <span className="text-primary-400">{projectData.ips.totalIps}</span>
          </div>
        </div>
      </div>

      <div className="h-[350px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis
              dataKey="displayIp"
              angle={-45}
              textAnchor="end"
              height={100}
              tick={{ fill: '#9CA3AF', fontSize: 11, fontFamily: 'monospace' }}
            />
            <YAxis
              tick={{ fill: '#9CA3AF' }}
              label={{ value: 'Количество запросов', angle: -90, position: 'insideLeft', fill: '#9CA3AF' }}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
            <Bar dataKey="count" radius={[8, 8, 0, 0]}>
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.isAnomaly ? '#EF4444' : '#10B981'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Легенда и статистика */}
      <div className="mt-4 space-y-3">
        <div className="flex items-center justify-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500 rounded"></div>
            <span className="text-dark-600">Нормальная активность</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-500 rounded"></div>
            <span className="text-dark-600">Аномальная активность</span>
          </div>
        </div>

        <div className="p-3 bg-dark-50 rounded-lg border border-dark-200">
          <div className="grid grid-cols-3 gap-4 text-center text-sm">
            <div>
              <div className="text-dark-500 mb-1">Аномалий</div>
              <div className="text-2xl font-bold text-red-400">
                {chartData.filter((d) => d.isAnomaly).length}
              </div>
            </div>
            <div>
              <div className="text-dark-500 mb-1">Всего в топе</div>
              <div className="text-2xl font-bold text-primary-400">{chartData.length}</div>
            </div>
            <div>
              <div className="text-dark-500 mb-1">Макс. запросов</div>
              <div className="text-2xl font-bold text-orange-400">
                {Math.max(...chartData.map((d) => d.count)).toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

